import { Box, Divider } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import { EditorContext, ModalContext, RicosContext } from 'ricos-context';
import type { PollData } from 'ricos-schema/next';
import { unreachable } from 'wix-rich-content-editor-common';
import { FooterActions, SidePanelLayout } from 'wix-rich-content-ui-components';

import classes from '../../../statics/styles/polls-settings-modal.scss';
import type { SettingsType } from '../../consts';
import { POLL_SETTINGS_MODAL_ID, SETTINGS_TAB } from '../../consts';
import { POLLS_SETTINGS_MODAL } from '../../dataHooks';
import type { PollPluginEditorConfig } from '../../types';
import { POLL_TYPE } from '../../types';
import { DesignSettingsSection } from './design-settings-section';
import { LayoutSettingsSection } from './layout-settings-section';
import { PollSettingsSection } from './poll-settings-section';
import { TabNavigation } from './tab-navigation';

export type SettingsModalProps = {
  nodeId: string;
  activeTab?: SettingsType;
  config: PollPluginEditorConfig;
};

export const SettingsModal: FC<SettingsModalProps> = ({
  nodeId,
  activeTab: initialTab,
  config,
}) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalContext);
  const { getEditorCommands } = useContext(EditorContext);

  const [activeTab, setActiveTab] = useState<SettingsType>(initialTab ?? 'layout');

  const [initialData] = useState<PollData>(() => getEditorCommands().getBlockComponentData(nodeId));
  const componentData = getEditorCommands().getBlockComponentData(nodeId);

  const updateRicosDocument = (data: PollData) => {
    queueMicrotask(() => {
      getEditorCommands().setBlock(nodeId, POLL_TYPE, { ...componentData, ...data, id: nodeId });
    });
  };

  const closeModal = () => modalService.closeModal(POLL_SETTINGS_MODAL_ID);

  const handleOnCancel = () => {
    updateRicosDocument(initialData);
    closeModal();
  };

  if (!componentData) {
    return null;
  }

  return (
    <SidePanelLayout
      dataHook={POLLS_SETTINGS_MODAL}
      onCloseButtonClick={closeModal}
      header={{
        showDivider: false,
        title: t('Poll_PollSettings_Common_Header'),
        children: (
          <>
            {!isMobile && <Divider />}
            <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
          </>
        ),
      }}
      content={{
        noPadding: true,
        children: (
          <Box marginLeft="SP4" marginRight="SP4" direction="vertical" className={classes.content}>
            {(() => {
              switch (activeTab) {
                case SETTINGS_TAB.layout:
                  return (
                    <LayoutSettingsSection
                      componentData={componentData}
                      updateRicosDocument={updateRicosDocument}
                    />
                  );
                case SETTINGS_TAB.design:
                  return (
                    <DesignSettingsSection
                      componentData={componentData}
                      updateRicosDocument={updateRicosDocument}
                    />
                  );
                case SETTINGS_TAB.settings:
                  return (
                    <PollSettingsSection
                      componentData={componentData}
                      config={config}
                      updateRicosDocument={updateRicosDocument}
                    />
                  );
                default:
                  unreachable(activeTab);
                  return null;
              }
            })()}
          </Box>
        ),
      }}
      footer={{ children: <FooterActions onSave={closeModal} onCancel={handleOnCancel} /> }}
    />
  );
};
